import React, { useEffect, useState } from 'react';
// import { AuthContext } from 'providers/AuthProvider';
import axiosInstance from 'utils/axiosInterceptors';
import { Button, Col, Dropdown, Form, Stack } from 'react-bootstrap';
import CustomPagination from 'components/pagination/CustomPagination';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useParams } from 'react-router-dom';
import { Row } from 'react-bootstrap';
import { TShelves } from 'utils/types';
import useClearLocalStorageOnPathChange from 'hooks/useClearLocalStorageOnPathChange';
import { useBranchContext } from 'providers/BranchProvider';
import CustomModal from 'components/modals/CustomModal';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { useAuth } from 'hooks/useAuth';
import { FeatureRoles, hasAnyRole } from 'utils/roleAccess';

type ShelfDetailsFormProps = {
  shelfDetails: TShelves;
  onChange: <K extends keyof TShelves>(field: K, value: TShelves[K]) => void;
};

const ShelfDetailsForm = React.memo(
  ({ shelfDetails, onChange }: ShelfDetailsFormProps) => {
    return (
      <>
        <Form>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="shelfCode">
              <Form.Label>Shelf Code</Form.Label>
              <Form.Control
                type="text"
                value={shelfDetails.shelfCode || ''}
                onChange={e => onChange('shelfCode', e.target.value)}
              />
            </Form.Group>
          </Row>
        </Form>
      </>
    );
  }
);

const columns: ColumnDef<TShelves>[] = [
  {
    accessorKey: 'shelfCode',
    header: 'Shelf Code'
  }
];

const LocationDetails = () => {
  const { id } = useParams();
  const [shelvesData, setShelvesData] = useState([]);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem('locationDetailsPage');
    return savedPage ? Number(savedPage) : 1;
  });
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const modalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => {},
    hide: () => {}
  });
  const { selectedBranch } = useBranchContext();
  const { token } = useAuth();
  const isAdmin = hasAnyRole(token?.roles, FeatureRoles.adminOnly);
  const [shelfDetails, setshelfDetails] = React.useState({
    shelfId: 0,
    shelfCode: ''
  });

  const getShelvesData = async (page: number) => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/locations/${id}/shelves?page=${page}&pageSize=${pageSize}`
      );
      const shelvesData = res.data.data;
      setShelvesData(shelvesData);
      setTotalPages(res.data.pageCount);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      localStorage.setItem('locationDetailsPage', newPage.toString());
    }
  };

  const handleSelect = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    localStorage.setItem('locationDetailsPage', '1');
  };

  const handleSubmit = async () => {
    try {
      await axiosInstance.post(
        `/api/v1/${selectedBranch?.branchDomain}/locations/${id}/shelves`,
        {
          shelfCode: shelfDetails.shelfCode
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      setshelfDetails({ shelfId: 0, shelfCode: '' });
      modalRef.current.hide();
      getShelvesData(currentPage);
      toast.success('Shelf Successfully Added', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.message, {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined
        });
      }
    }
  };

  const handleFormChange = <K extends keyof TShelves>(
    field: K,
    value: TShelves[K]
  ) => {
    setshelfDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const table = useAdvanceTable({
    data: shelvesData,
    columns,
    pageSize: 20,
    pagination: true,
    sortable: true
  });

  useEffect(() => {
    getShelvesData(currentPage);
    localStorage.setItem('locationDetailsPage', currentPage.toString());
  }, [currentPage, pageSize]);

  useClearLocalStorageOnPathChange();

  return (
    <>
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-baseline"
      >
        <Row lg="1">
          <h4>Shelves List</h4>
        </Row>
        {isAdmin && (
          <Row lg="auto">
            <Button
              variant="primary"
              className="w-10 mb-3 me-5"
              onClick={() => modalRef.current.show()}
            >
              Add Shelf
            </Button>
          </Row>
        )}
      </Stack>
      <div>
        <AdvanceTableProvider {...table}>
          <AdvanceTable
            tableProps={{
              size: 'lg',
              className: 'phoenix-table fs-9 mb-0 border-top border-translucent'
            }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
        </AdvanceTableProvider>
        <div className="d-flex mt-2 justify-content-end">
          <CustomPagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            totalPages={totalPages}
          />
          <Dropdown>
            <Dropdown.Toggle variant="phoenix-primary">
              {pageSize}
            </Dropdown.Toggle>
            <Dropdown.Menu className="py-2">
              <Dropdown.Item onClick={() => handleSelect(5)}> 5 </Dropdown.Item>
              <Dropdown.Item onClick={() => handleSelect(10)}>
                {' '}
                10{' '}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleSelect(15)}>
                {' '}
                15{' '}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleSelect(20)}>
                {' '}
                20{' '}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      {isAdmin && (
        <CustomModal
          ref={modalRef}
          title="Add Shelf"
          content={
            <ShelfDetailsForm
              shelfDetails={shelfDetails}
              onChange={handleFormChange}
            />
          }
          onCancel={() => modalRef.current.hide()}
          onSubmit={() => handleSubmit()}
        />
      )}
    </>
  );
};

export default LocationDetails;
